/**
 * PackageSelectionCards.jsx – Gemeinsame Paketauswahl-Komponente (v2)
 *
 * Redesign Phase 2B.6:
 * - Keine überlappenden Buttons mehr (GlobalMobileBackButton wird über NO_BACK_ROUTES ausgeblendet)
 * - Konsistente Kartenhöhen: Badge beeinflusst nicht die Kartenausrichtung
 * - Moderne visuelle Hierarchie: Schatten, Hover-States, ausgewählte Karte klar hervorgehoben
 * - Mobile-first: 1 Spalte auf Mobile, 3 auf Desktop
 * - Feature-Listen aus Billing Catalog (features_public)
 * - ?paket= URL-Parameter für Vorauswahl
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Zap, Briefcase, Star, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import usePublicPackages from '../hooks/usePublicPackages';

const PACKAGES_META = [
  {
    slug: 'starter',
    icon: Star,
    color: 'blue',
    tagline: 'Für den täglichen Betrieb',
    badge: null,
  },
  {
    slug: 'business',
    icon: Briefcase,
    color: 'indigo',
    tagline: 'Vollständige HR- & Betriebsverwaltung',
    badge: 'Beliebt',
  },
  {
    slug: 'professional_ai',
    icon: Zap,
    color: 'violet',
    tagline: 'Mit KI-Unterstützung',
    badge: null,
  },
];

const COLORS = {
  blue:   { ring: 'ring-blue-500',   shadow: 'shadow-blue-100 dark:shadow-blue-900/20',   bg: 'bg-blue-50 dark:bg-blue-950/20',   icon: 'text-blue-600',   iconBg: 'bg-blue-100 dark:bg-blue-900/30',   check: 'text-blue-500',   badge: 'bg-blue-600',  selected: 'ring-2 ring-blue-500' },
  indigo: { ring: 'ring-indigo-500', shadow: 'shadow-indigo-100 dark:shadow-indigo-900/20', bg: 'bg-indigo-50 dark:bg-indigo-950/20', icon: 'text-indigo-600', iconBg: 'bg-indigo-100 dark:bg-indigo-900/30', check: 'text-indigo-500', badge: 'bg-indigo-600', selected: 'ring-2 ring-indigo-500' },
  violet: { ring: 'ring-violet-500', shadow: 'shadow-violet-100 dark:shadow-violet-900/20', bg: 'bg-violet-50 dark:bg-violet-950/20', icon: 'text-violet-600', iconBg: 'bg-violet-100 dark:bg-violet-900/30', check: 'text-violet-500', badge: 'bg-violet-600', selected: 'ring-2 ring-violet-500' },
};

const PackageSelectionCards = ({
  selectedPackage,
  onSelectPackage,
  billingInterval,
  onIntervalChange,
  onSubscribe,
  loading = false,
  showBackButton = false,
  onBack,
}) => {
  const { packages, loading: pkgsLoading } = usePublicPackages();
  const [searchParams] = useSearchParams();

  // ?paket= URL-Parameter: Vorauswahl für Landingpage-Links
  const [initialized, setInitialized] = React.useState(false);
  React.useEffect(() => {
    const urlPaket = searchParams.get('paket');
    if (!initialized && urlPaket && PACKAGES_META.some(p => p.slug === urlPaket)) {
      onSelectPackage(urlPaket);
      setInitialized(true);
    }
  }, [searchParams, initialized, onSelectPackage]);

  const getPrice = (slug) => {
    const pkg = packages?.[slug];
    if (!pkg) return null;
    if (billingInterval === 'year') return pkg.price_yearly_eur;
    return pkg.price?.price_net_eur;
  };

  const getFeatures = (slug) => packages?.[slug]?.features_public ?? [];

  if (pkgsLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="package-selection-cards">

      {/* ── Intervall-Toggle ─────────────────────────────────────────────── */}
      <div className="flex p-1 gap-1 bg-muted rounded-xl max-w-xs mx-auto">
        {[
          { v: 'month', l: '🎉 1 Monat kostenlos' },
          { v: 'year',  l: 'Jährlich · 2 Monate gratis' },
        ].map(opt => (
          <button
            key={opt.v}
            type="button"
            onClick={() => onIntervalChange(opt.v)}
            data-testid={`interval-${opt.v}`}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-semibold transition-all ${
              billingInterval === opt.v
                ? 'bg-white dark:bg-gray-800 shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>

      {/* ── Paket-Karten ─────────────────────────────────────────────────── */}
      {/* Badge-Wrapper: sorgt dafür dass der Badge die Kartenausrichtung nicht beeinflusst */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
        {PACKAGES_META.map(({ slug, icon: Icon, color, tagline, badge }) => {
          const c = COLORS[color];
          const pkg = packages?.[slug];
          const price = getPrice(slug);
          const features = getFeatures(slug);
          const isSelected = selectedPackage === slug;

          return (
            <div key={slug} className="flex flex-col">
              {/* Badge-Zeile: feste Höhe für alle 3 Karten → gleicher Startpunkt */}
              <div className="h-7 flex items-center justify-center mb-1">
                {badge && (
                  <span className={`inline-block px-3 py-0.5 rounded-full text-[11px] font-bold text-white ${c.badge} shadow-sm`}>
                    {badge}
                  </span>
                )}
              </div>

              {/* Karte: flex-1 damit sie im Grid-Kontext die volle Zellhöhe füllt */}
              <button
                type="button"
                onClick={() => onSelectPackage(slug)}
                data-testid={`package-card-${slug}`}
                className={`
                  flex-1 flex flex-col w-full text-left rounded-2xl border transition-all duration-200
                  ${isSelected
                    ? `${c.bg} border-transparent ${c.selected} shadow-lg ${c.shadow}`
                    : 'bg-card border-border hover:border-muted-foreground/30 hover:shadow-md shadow-sm'
                  }
                `}
              >
                <div className="p-5 flex flex-col flex-1">
                  {/* Header */}
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`p-2 rounded-xl flex-shrink-0 ${isSelected ? c.iconBg : 'bg-muted'}`}>
                      <Icon className={`h-5 w-5 ${isSelected ? c.icon : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <p className="font-bold text-foreground">{pkg?.name ?? slug}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{tagline}</p>
                    </div>
                  </div>

                  {/* Preis */}
                  <div className="mb-5">
                    {price != null ? (
                      <>
                        <p className="text-3xl font-extrabold text-foreground tracking-tight">
                          {price.toFixed(2).replace('.', ',')}
                          <span className="text-base font-normal text-muted-foreground ml-1">€</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Netto / {billingInterval === 'year' ? 'Jahr' : 'Monat'}
                        </p>
                        {billingInterval === 'month' && (
                          <p className="text-xs font-semibold text-green-600 dark:text-green-400 mt-1">
                            🎉 1. Monat kostenlos – dann {price.toFixed(2).replace('.', ',')} €/Mon
                          </p>
                        )}
                        {billingInterval === 'month' && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            oder {(price * 10).toFixed(2).replace('.', ',')} € / Jahr
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">Wird geladen…</p>
                    )}
                  </div>

                  {/* Trennlinie */}
                  <div className={`h-px mb-4 ${isSelected ? `${c.ring.replace('ring-', 'bg-')} opacity-30` : 'bg-border'}`} />

                  {/* Feature-Liste */}
                  <ul className="space-y-2 flex-1">
                    {features.slice(0, 8).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isSelected ? c.check : 'text-green-500'}`} />
                        <span className={
                          i === 0 && f.startsWith('Alles aus')
                            ? 'font-semibold text-foreground'
                            : 'text-muted-foreground'
                        }>
                          {f}
                        </span>
                      </li>
                    ))}
                    {features.length > 8 && (
                      <li className="text-xs text-muted-foreground pl-6">
                        + {features.length - 8} weitere Funktionen
                      </li>
                    )}
                  </ul>

                  {/* Auswahl-Indikator – immer am Kartenende via mt-auto */}
                  <div className={`mt-auto pt-5`}>
                    <div className={`py-2 rounded-xl text-xs font-semibold text-center transition-all ${
                      isSelected
                        ? `bg-blue-600 text-white shadow-sm`
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {isSelected ? '✓ Ausgewählt' : 'Auswählen'}
                    </div>
                  </div>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Hinweis ──────────────────────────────────────────────────────── */}
      <p className="text-xs text-center text-muted-foreground px-4">
        Alle Preise Netto zzgl. 19% MwSt. · 15 Mitarbeiter pro Standort inklusive · Monatlich kündbar
      </p>

      {/* ── Aktions-Buttons ──────────────────────────────────────────────── */}
      <div className={`flex gap-3 ${showBackButton ? '' : 'max-w-sm mx-auto'}`}>
        {showBackButton && onBack && (
          <Button type="button" variant="outline" onClick={onBack} className="flex-1">
            Zurück
          </Button>
        )}
        <Button
          type="button"
          onClick={onSubscribe}
          disabled={loading || !selectedPackage}
          className="flex-1 h-12 text-base font-semibold"
          data-testid="package-subscribe-btn"
        >
          {loading
            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Wird vorbereitet…</>
            : 'Jetzt kostenpflichtig abonnieren →'
          }
        </Button>
      </div>
    </div>
  );
};

export default PackageSelectionCards;
